import { React, useEffect, useState, Fragment } from "react";
import classes from "../ClassRoster/LearnPointClassRoster.module.css";
import { useLocation } from "react-router-dom";
import SnackBar from "components/SnackBar/SnackBar";
import BackdropComponent from "../../../components/Spinner/Spinner";
import Grid from "@mui/material/Grid";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import PropTypes from "prop-types";
import {
  InputAdornment,
  Button,
  Paper,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  TableContainer,
  Table,
  TableBody,
  Box,
  TableCell,
  TableRow,
  IconButton,
  Popper,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import moment from "moment";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import {
  getClassRosterDetails,
  getClassRostersByTraineeEmail,
  updateClassRosterHeader,
  getSearchValue,
} from "../../../services/learnPointUserServices";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import UndoIcon from "@mui/icons-material/Undo";
import { Save } from "@mui/icons-material";
import {
  getConfigurationValues,
  getRosterConfig,
  searchEmployeeProfile,
} from "services/userServices";
import ClassRosterGrid from "components/LearnPoint/ClassRoster/ClassRosterGrid";
import { styled } from "@mui/material/styles";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DtrList from "components/LearnPoint/ClassRoster/DtrList";
import applicationUsageLog from "../../../utils/applicationUsagesLog";
import { CustomTooltip } from "components/RosterConfig/RosterConfigStyle";
import RosterGridV2 from "./ClassRosterGridV2";

const StyledTable = styled(Table)(({ theme }) => ({
  "& .MuiTableCell-root": {
    borderBottom: "none",
    fontFamily: "Urbanist, sans-serif",
  },
  "& .MuiTableBody-root .MuiTableRow-root:last-child": {
    borderBottom: "none",
  },
}));

const CustomPopper = (props) => {
  return <Popper {...props} placement="bottom-start" />;
};

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));

const CustomCheckIcon = () => <TaskAltIcon sx={{ color: "#3299FF" }} />;

const CustomUnCheckIcon = () => (
  <RadioButtonUncheckedIcon sx={{ color: "#3299FF" }} />
);

const CustomCheckbox = styled(Checkbox)(({ theme }) => ({
  padding: theme.spacing(0.5),
  "& .MuiSvgIcon-root": {
    fontSize: "1.75rem",
  },
}));

const getTTFFieldsValue = (isTtfEditDisabled, fieldValue) =>
  isTtfEditDisabled ? "TTF N/A per ASTT Roster config" : fieldValue;

const getNestingFieldsValue = (isNestingDisabled, fieldValue) =>
  isNestingDisabled ? "Nesting N/A per ASTT Roster config" : fieldValue;
const getTextFieldClassname = (isDisabled) =>
  isDisabled ? classes.disabledInput : classes.Container;

const validateAndFormatDate = (date) =>
  date ? formatDatebyGivenFormat(date, "YYYY-MM-DD") : "YYYY-MM-DD";

const formatDatebyGivenFormat = (date, format) =>
  moment.utc(date).format(format);

const formatString = (text) => String(text).trim();

const prepareRosterLabel = (item) => {
  let { ClassRosterId, ProjectName, Wave, Trainer, RosterStatus } = item;

  if (ProjectName === "" || !ProjectName) {
    ProjectName = "NA";
  } else {
    ProjectName = formatString(ProjectName);
  }

  if (Wave === "") {
    Wave = "NA";
  } else {
    Wave = formatString(Wave);
  }

  if (Trainer === "") {
    Trainer = "NA";
  } else {
    Trainer = formatString(Trainer);
  }

  if (RosterStatus === "Active") {
    RosterStatus = "IP";
  } else if (RosterStatus === "Cancelled") {
    RosterStatus = "CN";
  } else if (RosterStatus === "Pending Cancellation") {
    RosterStatus = "PC";
  } else if (RosterStatus === "Closed") {
    RosterStatus = "CL";
  } else {
    RosterStatus = formatString(RosterStatus);
  }

  return `${ClassRosterId} - ${ProjectName} - ${Wave} - (${Trainer}) - (${RosterStatus})`;
};

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={
      <ArrowForwardIosSharpIcon
        sx={{ fontSize: "1.5rem", color: "green", fontWeight: "bold" }}
      />
    }
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, .05)"
      : "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const TrainerPaperComponent = (props) => <Paper {...props} />;

const rosterStatusOptions = {
  1: "Include Active",
  4: "Include Closed",
  2: "Include Cancelled/Pending",
};

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  padding: "6px 20px",
  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

export default function ClassRoster() {
  const location = useLocation();
  // Roster id can arrive via router state (in-app navigation) or via the
  // `classRosterId` query param when opened in a new browser tab (router state
  // is not carried across tabs). Mirrors the SearchRosters URL-param pattern.
  const queryParams = new URLSearchParams(location.search);
  const resolvedClassRosterId = location.state ?? queryParams.get("classRosterId");
  const [expanded, setExpanded] = useState("panel1");
  const [loader, setLoader] = useState(false);
  const [classRosterDetails, setClassRosterDetails] = useState([]);
  const [classRequestDetails, setClassRequestDetails] = useState([]);
  const [ttfDate, setTtfDate] = useState("");
  const [prodDate, setProdDate] = useState("");
  const [nestingStartDate, setNestingStartDate] = useState("");
  const [ttfLocation, setTtfLocation] = useState("");
  const [nestingLocation, setNestingLocation] = useState("");
  const [ttfHostKey, setTtfHostKey] = useState("");
  const [ttfPasscode, setTtfPasscode] = useState("");
  const [productTrDate, setProductTrDate] = useState("");
  const [trainer, setTrainer] = useState("");
  const [trainerTextShow, setTrainerTextShow] = useState(true);
  const [searchResult, setSearchResult] = useState([]);
  const [selectedRosterStatuses, setSelectedRosterStatuses] = useState(["1"]);
  const [classRosterGridV2, setClassRosterGridV2] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchConfigValue = async () => {
      try {
        const response = await getConfigurationValues(
          "appsettings:lpClassRosterGridV2",
        );

        if (cancelled) return;

        if (response?.statusCode === 200) {
          if (!cancelled) {
            setClassRosterGridV2(
              response?.data?.lpClassRosterGridV2?.toString() === "1",
            );
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch config value:", error);
        }
      }
    };

    fetchConfigValue();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleCheckboxChange = (event, rosterStatusId) => {
    setSelectedRosterStatuses((prevStatuses) => {
      let newStatuses;
      if (event.target.checked) {
        if (rosterStatusId === 2) {
          newStatuses = [...prevStatuses, "2", "3"];
        } else {
          newStatuses = [...prevStatuses, rosterStatusId];
        }
      } else {
        if (rosterStatusId === "2") {
          newStatuses = prevStatuses.filter((id) => id !== "2" && id !== "3");
        } else {
          newStatuses = prevStatuses.filter((id) => id !== rosterStatusId);
        }
      }

      // Ensure at least one is selected
      if (newStatuses.length === 0) {
        setSnack({
          open: true,
          message:
            "Atleast 1 roster status must be selected (Default: Include Active).",
          severity: "info",
        });
        return ["1"]; // Default to '1' if none are selected
      }

      return newStatuses;
    });
  };

  const handleChangePanel = (panel) => (event, newExpanded) => {
    ResetValue("TrainerName");
    setExpanded(newExpanded ? panel : false);
  };
  const [disabledAccordionDetails, setDisabledAccordionDetails] =
    useState(true);

  const ItemHeading = styled(Paper)((props) => ({
    textAlign: "center",
    boxShadow: "none",
    fontWeight: 600,
    ...(props?.disabled && { paddingTop: 12 }),
  }));

  ItemHeading.propTypes = {
    disabled: PropTypes.bool,
  };
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const initialRosterValue = {
    rosterId: "",
    rosterLabel: "",
    rosterStatusId: 0,
  };
  const [filterRosterData, setFilterRosterData] = useState([
    initialRosterValue,
  ]);
  const [rosterData, setRosterData] = useState([initialRosterValue]);
  const [classRosterId, setClassRosterId] = useState(null);

  const [rosterOption, setRosterOption] = useState(initialRosterValue);
  const [rosterValue, setRosterValue] = useState(initialRosterValue);
  const [disableTTFEdit, setDisableTTFEdit] = useState(false);
  const [disableNestingEdit, setDisableNestingEdit] = useState(false);
  const [searchTraineeResults, setSearchTraineeResults] = useState([]);
  const [selectedTrainee, setSelectedTrainee] = useState({
    traineeOracleId: null,
    traineeName: "",
    traineeEmail: "",
  });

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        if (!cancelled) {
          setRosterOption(initialRosterValue);
        }

        // fetchRosterData already loads the roster config + details for the
        // location.state roster (see its location.state branch), so the
        // previous fetchRosterConfigDetails/rosterDetails block here was
        // firing the same requests a second time. Removed to eliminate the
        // redundant duplicate calls on redirect.
        await fetchRosterData("");

        if (cancelled) return;

        if (!cancelled) {
          applicationUsageLog(
            localStorage.getItem("User"),
            localStorage.getItem("EmailId"),
            "LearnPoint",
            "Class Roaster",
          );
          window.localStorage.removeItem("LPHome");
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch data:", error);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  const fetchRosterConfigDetails = async (rosterId) => {
    const response = await getRosterConfig(rosterId, false);
    setDisableTTFEdit(response?.data[0]?.ttfna);
    setDisableNestingEdit(response?.data[0]?.nestingNA);
  };

  const prepareRostersToLoad = (rostersData) => {
    if (rostersData !== undefined) {
      return rostersData;
    }
    return [{ ...initialRosterValue, rosterId: resolvedClassRosterId }];
  };

  useEffect(() => {
    const selectedIdsSet = new Set(selectedRosterStatuses);
    if (selectedRosterStatuses.length > 0) {
      if (selectedIdsSet.has("2")) {
        selectedIdsSet.add("3");
      }
      setFilterRosterData(
        rosterData.filter((roster) =>
          selectedIdsSet.has(roster.rosterStatusId.toString()),
        ),
      );
    } else {
      setFilterRosterData(rosterData);
    }

    if (!resolvedClassRosterId) {
      const timeout = setTimeout(() => {
        setClassRosterId(null);
        setRosterValue(initialRosterValue);
        setRosterOption(initialRosterValue);
        setClassRosterDetails([]);
        setClassRequestDetails([]);
        setProductTrDate("");
        setTtfDate("");
        setProdDate("");
        setNestingStartDate("");
        setTtfLocation("");
        setNestingLocation("");
        setTtfHostKey("");
        setTtfPasscode("");
        setTrainer("");
        setDisabledAccordionDetails(true);
        setDisableTTFEdit(false);
        setDisableNestingEdit(false);
      }, 1000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [selectedRosterStatuses, rosterData]);

  const fetchRosterData = async (filterByTrainee) => {
    setLoader(true);
    setRosterValue(initialRosterValue);
    let selectRosterList;
    selectRosterList = await getClassRostersByTraineeEmail(
      filterByTrainee?.toString(),
    );

    if (selectRosterList?.status === 200) {
      if (filterByTrainee) {
        setSnack({
          open: true,
          message: "Class rosters filtered by selected trainee.",
          severity: "success",
        });
      }
      const rosterDataList = selectRosterList?.data;
      const prepareRosterData = rosterDataList?.data?.map((item) => ({
        rosterId: item?.ClassRosterId,
        rosterLabel: prepareRosterLabel(item),
        rosterStatusId: item?.RosterStatusId,
      }));

      prepareRosterData.unshift(initialRosterValue);
      setRosterData(prepareRosterData);

      if (resolvedClassRosterId) {
        const findRosterData = prepareRosterData?.find(
          (item) => String(item?.rosterId) === String(resolvedClassRosterId),
        );

        const rosterToLoad = prepareRostersToLoad(findRosterData);
        if (rosterToLoad.rosterStatusId === 3) {
          setSelectedRosterStatuses(["2"]);
        } else {
          setSelectedRosterStatuses([`${rosterToLoad.rosterStatusId}`]);
        }

        setRosterValue(rosterToLoad);
        setRosterOption(rosterToLoad);
        setClassRosterId(rosterToLoad.rosterId);
        await fetchRosterConfigDetails(rosterToLoad.rosterId);
        await rosterDetails(rosterToLoad.rosterId);
      } else {
        setTimeout(() => {
          setRosterValue(initialRosterValue);
          setRosterOption(initialRosterValue);
          setClassRosterDetails([]);
          setClassRequestDetails([]);
          setClassRosterId(null);
          setProductTrDate("");
          setTtfDate("");
          setProdDate("");
          setNestingStartDate("");
          setTtfLocation("");
          setNestingLocation("");
          setTtfHostKey("");
          setTtfPasscode("");
          setTrainer("");
          setDisabledAccordionDetails(true);
          setDisableTTFEdit(false);
          setDisableNestingEdit(false);
        }, 1000);
      }
    } else {
      setSnack({
        open: true,
        message: "No rosters found.",
        severity: "warning",
      });
    }
    setLoader(false);
  };

  const handleReset = () => {
    setLoader(true);
    setTimeout(() => {
      setSelectedTrainee({
        traineeOracleId: null,
        traineeName: "",
        traineeEmail: "",
      });
      setSearchTraineeResults([]);
      setSelectedRosterStatuses(["1"]);

      setClassRosterDetails([]);
      setClassRequestDetails([]);
      setRosterData([initialRosterValue]);
      setFilterRosterData([initialRosterValue]);
      setRosterOption(initialRosterValue);

      setProductTrDate("");
      setTtfDate("");
      setProdDate("");
      setNestingStartDate("");
      setTtfLocation("");
      setNestingLocation("");
      setTtfHostKey("");
      setTtfPasscode("");
      setTrainer("");
      setDisabledAccordionDetails(true);
      setDisableTTFEdit(false);
      setDisableNestingEdit(false);
      fetchRosterData("");
    }, 1000);
    setLoader(false);
  };

  const handleSearch = async () => {
    if (rosterValue.rosterId === initialRosterValue.rosterId) {
      setSnack({
        open: true,
        message: "Please select class roster.",
        severity: "info",
      });
    } else {
      setClassRosterId(rosterValue.rosterId);
      await fetchRosterConfigDetails(rosterValue.rosterId);
      await rosterDetails(rosterValue.rosterId);
    }
  };

  const rosterDetails = async (rosterId) => {
    try {
      setLoader(true);
      const classRosterDetails = await getClassRosterDetails(rosterId);

      if (classRosterDetails?.message === "Success") {
        setClassRosterDetails(classRosterDetails?.data || {});
        setTtfDate(classRosterDetails?.data?.ttfDateTime || "");
        setProdDate(classRosterDetails?.data?.productionStartDate || "");
        setProductTrDate(
          classRosterDetails?.data?.productTrainingDateTime || "",
        );
        setNestingStartDate(
          classRosterDetails?.data?.nestingStartDateTime || "",
        );
        setTtfLocation(classRosterDetails?.data?.ttfZoomLink || "");
        setNestingLocation(classRosterDetails?.data?.nestingLocation || "");
        setTtfHostKey(classRosterDetails?.data?.ttfZoomHostKey || "");
        setTtfPasscode(classRosterDetails?.data?.ttfZoomPassword || "");
        setTrainer(classRosterDetails?.data?.trainer || "");
        setTrainerTextShow(true);
        setDisabledAccordionDetails(false);
        setClassRequestDetails(
          classRosterDetails?.data?.lpClassRequestProjects?.[0] || {},
        );
        setExpanded("panel2");
        setLoader(false);
      } else if (classRosterDetails?.statusCode === 404) {
        setLoader(false);
        setDisabledAccordionDetails(true);
        setSnack({
          open: true,
          message: "No record found.",
          severity: "info",
        });
      } else {
        // Any other response (e.g. body-level 500 / status:false, or an
        // empty/undefined payload) must still stop the loader and surface an
        // empty/error state — otherwise the spinner hangs indefinitely.
        setLoader(false);
        setClassRosterDetails({});
        setClassRequestDetails([]);
        setDisabledAccordionDetails(true);
        setSnack({
          open: true,
          message: "Unable to load roster details. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      setLoader(false);
      setDisabledAccordionDetails(true);
      setSnack({
        open: true,
        message: "Some internal error occurred.",
        severity: "error",
      });
    }
  };

  const ResetValue = async (key) => {
    if (key === "ttfDate") {
      setTtfDate(classRosterDetails.ttfDateTime ?? "");
    } else if (key === "ProductionStartDate") {
      setProdDate(classRosterDetails.productionStartDate);
    } else if (key === "ProductTrainingDate") {
      setProductTrDate(classRosterDetails.productTrainingDateTime);
    } else if (key === "nestingStartDate") {
      setNestingStartDate(classRosterDetails.nestingStartDateTime);
    } else if (key === "ttfLocation") {
      setTtfLocation(classRosterDetails.ttfZoomLink ?? "");
    } else if (key === "nestingLocation") {
      setNestingLocation(classRosterDetails.nestingLocation ?? "");
    } else if (key === "ttfHostKey") {
      setTtfHostKey(classRosterDetails.ttfZoomHostKey ?? "");
    } else if (key === "ttfPasscode") {
      setTtfPasscode(classRosterDetails.ttfZoomPassword ?? "");
    } else if (key === "TrainerName") {
      setTrainer(classRosterDetails.trainer ?? "");
      setSearchResult([]);
      setTrainerTextShow(true);
    }
  };

  const prepareProductionDatePayload = () => {
    let payload = { ProductTrainingDate: null, ProductTrainingTime: null };
    if (productTrDate !== classRosterDetails.productTrainingDateTime) {
      payload.ProductTrainingDate = formatDatebyGivenFormat(
        productTrDate,
        "YYYY-MM-DD",
      );
      payload.ProductTrainingTime = formatDatebyGivenFormat(
        productTrDate,
        "hh:mm A",
      );
    }

    return payload;
  };

  const prepareProductionStartDatePayload = () => {
    let payload = { ProductionDate: null };
    if (prodDate !== classRosterDetails.productionStartDate) {
      payload.ProductionDate = prodDate;
    }
    return payload;
  };

  const prepareTtfDatePayload = () => {
    let payload = { TTFDate: null, TTFTime: null };
    const defaultPayload = { TTFDate: "0001-01-01", TTFTime: "00:00 A" };

    if (isDateEmpty(ttfDate)) {
      return defaultPayload;
    } else if (ttfDate !== classRosterDetails.ttfDateTime) {
      payload.TTFDate = formatDatebyGivenFormat(ttfDate, "YYYY-MM-DD");
      payload.TTFTime = formatDatebyGivenFormat(ttfDate, "hh:mm A");
    }

    return payload;
  };

  const prepareNestingDatePayload = () => {
    const defaultPayload = {
      NestingDate: "0001-01-01",
      NestingTime: "00:00 A",
    };
    let payload = { NestingDate: null, NestingTime: null };

    if (isDateEmpty(nestingStartDate)) {
      return defaultPayload;
    } else if (nestingStartDate !== classRosterDetails.nestingStartDateTime) {
      payload.NestingDate = formatDatebyGivenFormat(
        nestingStartDate,
        "YYYY-MM-DD",
      );
      payload.NestingTime = formatDatebyGivenFormat(
        nestingStartDate,
        "hh:mm A",
      );
    }

    return payload;
  };

  const prepareTextPayload = (textValue, sourceKey, payloadKey) => {
    const defaultPayload = { [payloadKey]: "blank" };
    let payload = { [payloadKey]: null };
    if (isTextEmpty(textValue)) {
      return defaultPayload;
    } else if (textValue !== classRosterDetails[sourceKey]) {
      payload[payloadKey] = textValue;
    }
    return payload;
  };

  const isDateEmpty = (dateValue) =>
    dateValue === undefined ||
    dateValue === "undefined" ||
    dateValue === " " ||
    dateValue === null ||
    dateValue.trim().length === 0;

  const isTextEmpty = (textValue) =>
    textValue === " " || textValue === null || textValue.trim().length === 0;

  const prepareSavePayload = (key) => {
    let payload;
    switch (key) {
      case "ProductTrainingDate":
        payload = prepareProductionDatePayload();
        break;
      case "ProductionStartDate":
        payload = prepareProductionStartDatePayload();
        break;
      case "ttfDate":
        payload = prepareTtfDatePayload();
        break;
      case "ttfLocation":
        payload = prepareTextPayload(ttfLocation, "ttfZoomLink", "TTFZoomLink");
        break;
      case "ttfHostKey":
        payload = prepareTextPayload(
          ttfHostKey,
          "ttfZoomHostKey",
          "TTFZoomHostKey",
        );
        break;
      case "ttfPasscode":
        payload = prepareTextPayload(
          ttfPasscode,
          "ttfZoomPassword",
          "TTFZoomPassword",
        );

        break;
      case "nestingStartDate":
        payload = prepareNestingDatePayload();
        break;
      case "nestingLocation":
        payload = prepareTextPayload(
          nestingLocation,
          "nestingLocation",
          "nestingLocation",
        );

        break;
      default:
        break;
    }
    return payload;
  };

  const SaveValue = async (key) => {
    try {
      setLoader(true);
      if (isDateEmpty(prodDate)) {
        setLoader(false);
        setSnack({
          open: true,
          message: "Production Start Date required.",
          severity: "error",
        });
      } else if (isDateEmpty(productTrDate)) {
        setLoader(false);
        setSnack({
          open: true,
          message: "Product training date required.",
          severity: "error",
        });
      } else {
        const payload = prepareSavePayload(key);
        const response = await updateClassRosterHeader(
          rosterOption.rosterId,
          localStorage.getItem("User"),
          payload,
        );
        if (response.message === "Success") {
          setLoader(false);
          setSnack({
            open: true,
            message: "Successfully updated.",
            severity: "success",
          });
          rosterDetails(rosterOption.rosterId);
        } else {
          setLoader(false);
          setSnack({
            open: true,
            message: "Some internal error occurred.",
            severity: "error",
          });
        }
      }
    } catch (e) {
      setLoader(false);
    }
  };

  const handleDateChange = (event, key) => {
    if (key === "ProductionStartDate") {
      setProdDate(event.target.value);
    } else if (key === "ttfDate") {
      setTtfDate(event.target.value);
    } else if (key === "ProductTrainingDate") {
      setProductTrDate(event.target.value);
    } else if (key === "nestingStartDate") {
      setNestingStartDate(event.target.value);
    }
  };
  const handleTextChange = (event, key) => {
    if (key === "ttfLocation") {
      setTtfLocation(event.target.value);
    } else if (key === "ttfHostKey") {
      setTtfHostKey(event.target.value);
    } else if (key === "ttfPasscode") {
      setTtfPasscode(event.target.value);
    } else if (key === "TrainerName") {
      setTrainer(event.target.value);
    } else if (key === "nestingLocation") {
      setNestingLocation(event.target.value);
    }
  };

  const searchHandler = async (key) => {
    try {
      setLoader(true);
      if (key === "TrainerName") {
        if (trainer !== "" && trainer.length > 2) {
          const valueSearch = await getSearchValue(trainer);
          if (valueSearch.message === "Success") {
            setSearchResult(valueSearch.data);
            setTrainerTextShow(() => false);
            setLoader(() => false);
          } else {
            setTrainerTextShow(() => true);
            setLoader(() => false);
            setSnack({
              open: true,
              message: "Trainer name not found",
              severity: "error",
            });
          }
        } else {
          setLoader(false);
          setSnack({
            open: true,
            message: "Please enter at least 3 characters",
            severity: "error",
          });
        }
      }
    } catch (e) {
      setLoader(false);
    }
  };

  const SaveTrainerValue = async (event, value) => {
    try {
      setLoader(true);
      if (classRosterDetails.trainerId === value.OracleId) {
        setLoader(false);
        setSnack({
          open: true,
          message: "Please check Trainer selection.",
          severity: "error",
        });
      } else {
        let payload = {
          ProductionDate: null,
          TTFDate: null,
          TTFTime: null,
          TTFZoomLink: null,
          TTFZoomHostKey: null,
          TTFZoomPassword: null,
          TrainerID: value.OracleId,
        };

        const response = await updateClassRosterHeader(
          rosterOption.rosterId,
          localStorage.getItem("User"),
          payload,
        );
        if (response.message === "Success") {
          rosterDetails(rosterOption.rosterId);
          setSearchResult(() => []);
          setLoader(() => false);
          setSnack({
            open: true,
            message: "Trainer successfully updated.",
            severity: "success",
          });
        } else {
          setLoader(false);
          setSnack({
            open: true,
            message: "Some internal error occured during Trainer update.",
            severity: "error",
          });
        }
      }
    } catch (e) {
      setLoader(false);
    }
  };

  const handleTraineeSearch = async (event, searchTraineeQuery) => {
    if (searchTraineeQuery) {
      if (searchTraineeQuery.length < 3) {
        setSnack({
          open: true,
          message: "Search criteria must be at least 3 characters in length.",
          severity: "error",
        });
        return;
      }
      setLoader(true);
      const searchResult = await searchEmployeeProfile(searchTraineeQuery);
      if (searchResult.status === true) {
        let searchResultDetails;

        if (!isNaN(searchTraineeQuery)) {
          searchResultDetails = searchResult.data.map((emp) => ({
            OracleId: emp.OracleId,
            Display:
              (emp.Name ?? "") + (emp.OracleId ? ` (${emp.OracleId})` : ""),
            Name: emp.Name,
            Email: emp.Email,
          }));
        } else {
          searchResultDetails = searchResult.data.map((emp) => ({
            OracleId: emp.OracleId,
            Display: emp.Display,
            Name: emp.Name,
            Email: emp.Email,
          }));
        }

        setSearchTraineeResults(searchResultDetails);

        setSnack({
          open: true,
          message: "Results found matching the search criteria.",
          severity: "success",
        });
      } else {
        setSnack({
          open: true,
          message: "No result found matching with search criateria.",
          severity: "error",
        });
      }
      setLoader(false);
    }
  };

  const handleTraineeSelect = (event, value) => {
    if (value) {
      if (value?.Email) {
        setSelectedTrainee({
          traineeOracleId: value.OracleId,
          traineeName: value.Name,
          traineeEmail: value.Email,
        });
        fetchRosterData(value.Email);
      }
    }
    setSearchTraineeResults([]);
  };

  const handleTraineeInputChange = (event, inputValue) => {
    if (inputValue === "") {
      if (selectedTrainee.traineeEmail) {
        fetchRosterData("");
      }
      setSelectedTrainee({
        traineeOracleId: null,
        traineeName: "",
        traineeEmail: "",
      });
    }
  };

  const handleTraineeSearchInputChange = (key, value) => {
    setSelectedTrainee((prevState) => ({ ...prevState, [key]: value }));
  };

  return (
    <>
      {loader ? <BackdropComponent /> : null}
      <Accordion
        expanded={expanded === "panel1"}
        onChange={handleChangePanel("panel1")}
      >
        <AccordionSummary
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          expandIcon={<ExpandMoreIcon />}
        >
          <b>Class Details</b>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Box
              display="flex"
              justifyContent="space-evenly"
              alignItems="center"
              spacing={1}
            >
              <StyledTable>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <b>Class Rosters :</b>
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        disablePortal
                        freeSolo
                        openOnFocus
                        options={filterRosterData}
                        value={rosterValue || ""}
                        getOptionLabel={(item) =>
                          item?.rosterLabel?.toString() || ""
                        }
                        onChange={(event, value) => {
                          if (value) {
                            setRosterOption(value);
                            setRosterValue(value);
                          } else {
                            setRosterValue(initialRosterValue);
                          }
                        }}
                        sx={{
                          width: 400,
                          opacity: 1,
                          borderWidth: 1,
                          borderStyle: "solid",
                          borderColor: "gray",
                          borderRadius: 32,
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Roster - Project - Wave - Trainer - Status"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 32,
                                border: "1px solid #d3d3d3",
                                "& fieldset": {
                                  border: "none",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell rowSpan={2}>
                      {Object.entries(rosterStatusOptions).map(
                        ([rosterStatusId, rosterStatus]) => (
                          // `React` is imported as a named import here, not default —
                          // use the Fragment named import instead of React.Fragment.
                          <Fragment key={rosterStatusId}>
                            <FormControlLabel
                              sx={{
                                width: 200,
                              }}
                              control={
                                <CustomCheckbox
                                  checked={selectedRosterStatuses.includes(
                                    rosterStatusId,
                                  )}
                                  onChange={(event) =>
                                    handleCheckboxChange(event, rosterStatusId)
                                  }
                                  checkedIcon={<CustomCheckIcon />}
                                  icon={<CustomUnCheckIcon />}
                                />
                              }
                              label={rosterStatus}
                            />
                            <br></br>
                          </Fragment>
                        ),
                      )}
                    </TableCell>
                    <TableCell>
                      <b>Trainee search:</b>
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        freeSolo
                        options={searchTraineeResults}
                        getOptionLabel={(option) => option.Display || ""}
                        onInputChange={handleTraineeInputChange}
                        onChange={handleTraineeSelect}
                        inputValue={selectedTrainee.traineeName || ""}
                        PopperComponent={CustomPopper}
                        sx={{
                          width: 360,
                          opacity: 1,
                          borderWidth: 1,
                          borderStyle: "solid",
                          borderColor: "gray",
                          borderRadius: 32,
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            name="traineeName"
                            size="small"
                            variant="outlined"
                            placeholder="Search Trainee"
                            value={selectedTrainee.traineeName || ""}
                            onChange={(event) =>
                              handleTraineeSearchInputChange(
                                event.target.name,
                                event.target.value,
                              )
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 32,
                                border: "1px solid #d3d3d3",
                                "& fieldset": {
                                  border: "none",
                                },
                              },
                            }}
                            InputProps={{
                              maxLength: 50,
                              ...params.InputProps,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <CustomTooltip title={"Search"}>
                                    <IconButton
                                      id="icon-button-search"
                                      type="button"
                                      onClick={(event) =>
                                        handleTraineeSearch(
                                          event,
                                          selectedTrainee.traineeName,
                                        )
                                      }
                                      color="primary"
                                    >
                                      <SearchIcon />
                                    </IconButton>
                                  </CustomTooltip>
                                  {params.InputProps.endAdornment}
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>&nbsp;</TableCell>
                    <TableCell>
                      <CustomButton
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={handleSearch}
                      >
                        Search
                      </CustomButton>
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      <CustomOutlinedButton
                        variant="outlined"
                        startIcon={<RestartAltIcon />}
                        onClick={handleReset}
                      >
                        Reset
                      </CustomOutlinedButton>
                    </TableCell>
                    <TableCell>&nbsp;</TableCell>
                    <TableCell>
                      <CustomButton
                        variant="contained"
                        startIcon={<SendIcon />}
                        target="_blank"
                        onClick={() => {
                          window.open(
                            `${window.location.origin}/home`,
                            "_blank",
                          );
                        }}
                      >
                        Search for Class Roster via Parent Client and Wave
                      </CustomButton>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </StyledTable>
            </Box>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === "panel2"}
        onChange={handleChangePanel("panel2")}
      >
        <AccordionSummary
          aria-controls="searchResult"
          id="searchResult"
          expandIcon={<ExpandMoreIcon />}
          disabled={disabledAccordionDetails}
        >
          <b>Search Results</b>
        </AccordionSummary>
        <AccordionDetails>
          <Grid
            container
            direction="row"
            justifyContent="space-evenly"
            alignItems="center"
          >
            <Grid
              item
              xs={1.5}
              sx={{ textAlign: "center", fontWeight: "bold", marginBottom: 1 }}
            >
              Client
            </Grid>
            <Grid
              item
              xs={1.5}
              sx={{ textAlign: "center", fontWeight: "bold", marginBottom: 1 }}
            >
              Site
            </Grid>
            <Grid
              item
              xs={2.5}
              sx={{ textAlign: "center", fontWeight: "bold", marginBottom: 1 }}
            >
              Trainer
            </Grid>
            <Grid
              item
              xs={1.5}
              sx={{ textAlign: "center", fontWeight: "bold", marginBottom: 1 }}
            >
              Production Start Date
            </Grid>
            <Grid
              item
              xs={2}
              sx={{ textAlign: "center", fontWeight: "bold", marginBottom: 1 }}
            >
              OG-FDO
            </Grid>
            <Grid
              item
              xs={2}
              sx={{ textAlign: "center", fontWeight: "bold", marginBottom: 1 }}
            >
              TTF Date/Time
            </Grid>

            <Grid item xs={1.5}>
              <TextField
                disabled
                size="small"
                value={classRequestDetails.client || "NA"}
                fullWidth
              />
            </Grid>
            <Grid item xs={1.5}>
              <TextField
                disabled
                size="small"
                value={classRosterDetails.site || "NA"}
                fullWidth
              />
            </Grid>
            <Grid item xs={2.5}>
              {trainerTextShow && (
                <TextField
                  id="outlined-start-adornment"
                  className={classes.searchField}
                  type="search"
                  size="small"
                  value={trainer}
                  onChange={(event) => handleTextChange(event, "TrainerName")}
                  onKeyUp={(event) => {
                    if (event.key === "Enter") {
                      searchHandler("TrainerName");
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          className={classes.iconSave}
                          onClick={() => searchHandler("TrainerName")}
                        />
                        <UndoIcon
                          className={classes.iconReset}
                          onClick={() => ResetValue("TrainerName")}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              {!trainerTextShow && (
                <Autocomplete
                  size="small"
                  disablePortal
                  freeSolo={true}
                  open={!trainerTextShow}
                  className={classes.searchField}
                  options={searchResult}
                  onChange={SaveTrainerValue}
                  getOptionLabel={(option) => `${option.Name} ${option.Email}`}
                  renderInput={(params) => <TextField {...params} />}
                  PaperComponent={TrainerPaperComponent}
                />
              )}
            </Grid>
            <Grid item xs={1.5}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <TextField
                  className={classes.Container}
                  id="productionStartDate"
                  size="small"
                  type="date"
                  value={validateAndFormatDate(prodDate)}
                  onChange={(event) =>
                    handleDateChange(event, "ProductionStartDate")
                  }
                  onKeyDown={(event) =>
                    handleDateChange(
                      event.key === "Backspace" ? "" : event.target.value,
                    )
                  }
                  InputLabelProps={{ shrink: true, inputFormat: "YYYY-MM-DD" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <Save
                          className={classes.iconSave}
                          onClick={() => SaveValue("ProductionStartDate")}
                        />
                        <UndoIcon
                          className={classes.iconReset}
                          onClick={() => ResetValue("ProductionStartDate")}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={2}>
              <TextField
                disabled
                size="small"
                value={classRosterDetails.oG_FDO || "NA"}
                fullWidth
              />
            </Grid>
            <Grid item xs={2}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <TextField
                  className={getTextFieldClassname(disableTTFEdit)}
                  disabled={disableTTFEdit}
                  id="datetime-local"
                  size="small"
                  {...(!disableTTFEdit && { type: "datetime-local" })}
                  value={getTTFFieldsValue(disableTTFEdit, ttfDate)}
                  onChange={(event) => handleDateChange(event, "ttfDate")}
                  onKeyDown={(event) =>
                    handleDateChange(
                      event.key === "Backspace" ? "" : event.target.value,
                    )
                  }
                  InputLabelProps={{
                    shrink: true,
                    inputFormat: "YYYY-MM-DD : HH:MM",
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        {!disableTTFEdit && (
                          <Save
                            className={classes.iconSave}
                            onClick={() => SaveValue("ttfDate")}
                          />
                        )}
                        {!disableTTFEdit && (
                          <UndoIcon
                            className={classes.iconReset}
                            onClick={() => ResetValue("ttfDate")}
                          />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid
              item
              xs={1.75}
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                marginTop: 2,
                marginBottom: 1,
              }}
            >
              TTF Location
            </Grid>
            <Grid
              item
              xs={1.75}
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                marginTop: 2,
                marginBottom: 1,
              }}
            >
              TTF Host Key
            </Grid>
            <Grid
              item
              xs={1.5}
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                marginTop: 2,
                marginBottom: 1,
              }}
            >
              TTF Passcode
            </Grid>
            <Grid
              item
              xs={2}
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                marginTop: 2,
                marginBottom: 1,
              }}
            >
              Product Training Date/Time
            </Grid>
            <Grid
              item
              xs={2}
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                marginTop: 2,
                marginBottom: 1,
              }}
            >
              Nesting Start Date
            </Grid>
            <Grid
              item
              xs={2}
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                marginTop: 2,
                marginBottom: 1,
              }}
            >
              Nesting Location
            </Grid>

            <Grid item xs={1.75}>
              <TextField
                className={getTextFieldClassname(disableTTFEdit)}
                disabled={disableTTFEdit}
                size="small"
                value={getTTFFieldsValue(disableTTFEdit, ttfLocation)}
                onChange={(event) => handleTextChange(event, "ttfLocation")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      {!disableTTFEdit && (
                        <Save
                          className={classes.iconSave}
                          onClick={() => SaveValue("ttfLocation")}
                        />
                      )}
                      {!disableTTFEdit && (
                        <UndoIcon
                          className={classes.iconReset}
                          onClick={() => ResetValue("ttfLocation")}
                        />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={1.75}>
              <TextField
                className={getTextFieldClassname(disableTTFEdit)}
                disabled={disableTTFEdit}
                size="small"
                value={getTTFFieldsValue(disableTTFEdit, ttfHostKey)}
                onChange={(event) => handleTextChange(event, "ttfHostKey")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      {!disableTTFEdit && (
                        <Save
                          className={classes.iconSave}
                          onClick={() => SaveValue("ttfHostKey")}
                        />
                      )}
                      {!disableTTFEdit && (
                        <UndoIcon
                          className={classes.iconReset}
                          onClick={() => ResetValue("ttfHostKey")}
                        />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={1.5}>
              <TextField
                className={getTextFieldClassname(disableTTFEdit)}
                disabled={disableTTFEdit}
                size="small"
                value={getTTFFieldsValue(disableTTFEdit, ttfPasscode)}
                onChange={(event) => handleTextChange(event, "ttfPasscode")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      {!disableTTFEdit && (
                        <Save
                          className={classes.iconSave}
                          onClick={() => SaveValue("ttfPasscode")}
                        />
                      )}
                      {!disableTTFEdit && (
                        <UndoIcon
                          className={classes.iconReset}
                          onClick={() => ResetValue("ttfPasscode")}
                        />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={2}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <TextField
                  className={classes.Container}
                  id="datetime-local"
                  type="datetime-local"
                  size="small"
                  value={productTrDate}
                  onChange={(event) =>
                    handleDateChange(event, "ProductTrainingDate")
                  }
                  onKeyDown={(event) =>
                    handleDateChange(
                      event.key === "Backspace" ? "" : event.target.value,
                    )
                  }
                  InputLabelProps={{
                    shrink: true,
                    inputFormat: "YYYY-MM-DD : HH:MM",
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <Save
                          className={classes.iconSave}
                          onClick={() => SaveValue("ProductTrainingDate")}
                        />
                        <UndoIcon
                          className={classes.iconReset}
                          onClick={() => ResetValue("ProductTrainingDate")}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={2}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <TextField
                  id="datetime-local"
                  {...(!disableNestingEdit && { type: "datetime-local" })}
                  className={getTextFieldClassname(disableNestingEdit)}
                  disabled={disableNestingEdit}
                  size="small"
                  value={
                    getNestingFieldsValue(
                      disableNestingEdit,
                      nestingStartDate,
                    ) || "NA"
                  }
                  onChange={(event) =>
                    handleDateChange(event, "nestingStartDate")
                  }
                  onKeyDown={(event) =>
                    handleDateChange(
                      event.key === "Backspace" ? "" : event.target.value,
                    )
                  }
                  InputLabelProps={{
                    shrink: true,
                    inputFormat: "YYYY-MM-DD : HH:MM",
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        {!disableNestingEdit && (
                          <Save
                            className={classes.iconSave}
                            onClick={() => SaveValue("nestingStartDate")}
                          />
                        )}
                        {!disableNestingEdit && (
                          <UndoIcon
                            className={classes.iconReset}
                            onClick={() => ResetValue("nestingStartDate")}
                          />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={2}>
              <TextField
                className={getTextFieldClassname(disableNestingEdit)}
                disabled={disableNestingEdit}
                size="small"
                value={getNestingFieldsValue(
                  disableNestingEdit,
                  nestingLocation,
                )}
                onChange={(event) => handleTextChange(event, "nestingLocation")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      {!disableNestingEdit && (
                        <Save
                          className={classes.iconSave}
                          onClick={() => SaveValue("nestingLocation")}
                        />
                      )}
                      {!disableNestingEdit && (
                        <UndoIcon
                          className={classes.iconReset}
                          onClick={() => ResetValue("nestingLocation")}
                        />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === "panel3"}
        onChange={handleChangePanel("panel3")}
      >
        <AccordionSummary
          aria-controls="rosterList"
          id="rosterList"
          expandIcon={<ExpandMoreIcon />}
          disabled={disabledAccordionDetails}
        >
          <b>Roster List</b>
        </AccordionSummary>
        <AccordionDetails>
          {classRosterGridV2 ? (
            <RosterGridV2
              key={classRosterId}
              classRosterId={classRosterId}
            />
          ) : (
            <ClassRosterGrid classRosterId={classRosterId}></ClassRosterGrid>
          )}
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === "panel4"}
        onChange={handleChangePanel("panel4")}
      >
        <AccordionSummary
          aria-controls="dtrList"
          id="dtrList"
          expandIcon={<ExpandMoreIcon />}
          disabled={disabledAccordionDetails}
        >
          <b>DTR List</b>
        </AccordionSummary>
        <AccordionDetails>
          <DtrList
            classRosterId={classRosterId !== null ? classRosterId : ""}
          ></DtrList>
        </AccordionDetails>
      </Accordion>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
    </>
  );
}

ClassRoster.propTypes = {
  disabled: PropTypes.bool,
};
